import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { RestaurantService } from '../../../../services/restaurant.service';
import { FNFResponse } from '../../../../models/response';

@Component({
  selector: 'app-add-category',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIcon],
  templateUrl: './add-category.component.html',
  styleUrl: './add-category.component.css',
})
export class AddCategoryComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  restaurantService = inject(RestaurantService);

  type: any = null;
  loading = true;
  name = '';
  description = '';
  error = '';

  ngOnInit() {
    this.route.paramMap.subscribe((params) => this.load(params.get('typeId')));
  }

  private load(typeId: string | null) {
    this.loading = true;
    this.restaurantService.getRestaurantTypeAndCategories().subscribe({
      next: (response: FNFResponse<any>) => {
        const types = response?.data || [];
        this.type = types.find((t: any) => String(t.id) === String(typeId)) || null;
        this.loading = false;
        if (!this.type) {
          this.toastr.error('That type no longer exists.', 'Error');
          this.cancel();
        }
      },
      error: () => {
        this.toastr.error('Unable to load the type. Please try again later.', 'Error');
        this.loading = false;
        this.cancel();
      },
    });
  }

  get existingCategories(): any[] {
    return this.type?.categories || [];
  }

  /**
   * Reached from Add Menu as well as from Types & Categories; returnTo carries
   * the caller so the menu being written is not left behind.
   */
  private get returnUrl(): string {
    return this.route.snapshot.queryParamMap.get('returnTo') || '/main/type-and-categories';
  }

  cancel() {
    this.router.navigateByUrl(this.returnUrl);
  }

  private validate(): boolean {
    const name = this.name.trim();
    if (!name) {
      this.error = 'Category name cannot be empty.';
      return false;
    }
    if (name.length < 3 || name.length > 50) {
      this.error = 'Category name must be between 3 and 50 characters.';
      return false;
    }
    if (
      this.existingCategories.some((c: any) => c.name?.trim().toLowerCase() === name.toLowerCase())
    ) {
      this.error = 'That category already exists under this type.';
      return false;
    }
    this.error = '';
    return true;
  }

  save() {
    if (!this.validate()) {
      return;
    }

    const category: any = { name: this.name.trim() };
    // Categories have never carried a description server-side, so one is only
    // sent when the user wrote something; an untouched form posts what it
    // always did.
    const description = this.description.trim();
    if (description) {
      category.description = description;
    }

    this.spinner.show();
    this.restaurantService.addTypeCategory(this.type.id, [category]).subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || 'No data found!', 'Error');
          return;
        }
        this.toastr.success(response.data.message || 'Category added successfully', 'Success');
        this.cancel();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
